<template>
  <div class="clients-info-page">
    <v-alert v-if="pageError" type="error" variant="tonal" closable class="mb-4" @click:close="pageError = ''">
      {{ pageError }}
    </v-alert>

    <v-card variant="outlined" class="mb-6">
      <v-card-title class="d-flex align-center flex-wrap ga-2">
        <v-icon icon="mdi-server-network" class="mr-1" />
        Клиенты
        <v-spacer />
        <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
          Добавить клиента
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="clients"
        :loading="clientsLoading"
        :items-per-page="10"
        density="compact"
        no-data-text="Клиенты не добавлены"
      >
        <template #item.name="{ item }">
          <span :class="{ 'text-medium-emphasis': toBoolean(item.hidden) }">{{ item.name }}</span>
          <v-chip v-if="toBoolean(item.hidden)" size="x-small" variant="tonal" class="ml-2">Скрыт</v-chip>
        </template>
        <template #item.url="{ item }">
          <a v-if="serverUrl(item.url)" :href="serverUrl(item.url)" target="_blank" rel="noopener noreferrer">{{ item.url }}</a>
          <span v-else-if="item.url" class="text-error">{{ item.url }} (некорректный URL)</span>
          <span v-else class="text-medium-emphasis">не указан</span>
        </template>
        <template #item.type="{ item }">
          <div class="d-flex ga-1 flex-wrap">
            <v-chip v-if="toBoolean(item.branch)" size="x-small" color="primary" variant="tonal">Филиал</v-chip>
            <v-chip v-if="toBoolean(item.finance)" size="x-small" color="secondary" variant="tonal">Частная</v-chip>
            <span v-if="!toBoolean(item.branch) && !toBoolean(item.finance)" class="text-medium-emphasis">—</span>
          </div>
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex ga-1 justify-end">
            <v-btn icon="mdi-refresh" size="small" variant="text" :disabled="!item.url || toBoolean(item.hidden)" @click="loadServerInfo(item)" />
            <v-btn icon="mdi-pencil" size="small" variant="text" color="primary" @click="openEditDialog(item)" />
            <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="confirmDelete(item)" />
          </div>
        </template>
      </v-data-table>
    </v-card>

    <div class="d-flex align-center flex-wrap ga-2 mb-3">
      <div>
        <div class="text-h6">Состояние серверов</div>
        <div class="text-body-2 text-medium-emphasis">Данные запрашиваются непосредственно с серверов клиентов.</div>
      </div>
      <v-spacer />
      <v-btn
        variant="tonal"
        prepend-icon="mdi-refresh"
        :loading="serversLoading"
        :disabled="activeClients.length === 0"
        @click="loadAllServerInfo"
      >
        Обновить все
      </v-btn>
    </div>

    <v-alert v-if="!clientsLoading && activeClients.length === 0" type="info" variant="tonal">
      Нет активных клиентов с указанным URL.
    </v-alert>

    <v-row v-else>
      <v-col v-for="client in activeClients" :key="client.id" cols="12" sm="6" lg="3">
        <v-card variant="outlined" height="100%" class="server-card">
          <v-card-title class="server-card-header">
            <div class="server-heading-row">
              <v-icon :icon="statusIcon(client.id)" :color="statusColor(client.id)" />
              <v-btn icon="mdi-refresh" size="x-small" variant="text" :loading="serverStatus(client.id).loading" @click="loadServerInfo(client)" />
            </div>
            <div class="server-title">
              <div class="font-weight-bold">{{ client.name }}</div>
              <a v-if="serverUrl(client.url)" :href="serverUrl(client.url)" target="_blank" rel="noopener noreferrer" class="text-caption">{{ client.url }}</a>
              <span v-else class="text-caption text-error">{{ client.url }}</span>
            </div>
          </v-card-title>

          <v-divider />
          <v-card-text class="py-0">
            <div v-if="serverStatus(client.id).loading" class="d-flex align-center ga-3 py-5">
              <v-progress-circular indeterminate color="primary" size="24" />
              <span>Запрос состояния…</span>
            </div>

            <v-alert v-else-if="serverStatus(client.id).error" type="error" variant="tonal" density="compact">
              <div class="font-weight-bold">Код: {{ serverStatus(client.id).code }}</div>
              <div>{{ serverStatus(client.id).error }}</div>
            </v-alert>

            <template v-else-if="serverStatus(client.id).response">
              <v-alert
                v-if="Object.keys(serverStatus(client.id).response.data || {}).length === 0"
                type="success"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                Проблем не обнаружено
              </v-alert>

              <div
                v-for="(entry, key) in serverStatus(client.id).response.data || {}"
                :key="key"
                class="status-row"
                :title="plainText(entry.tooltip)"
              >
                <v-icon icon="mdi-alert-circle" color="error" size="18" />
                <div>
                  <div class="text-caption text-medium-emphasis">{{ entry.title || key }}</div>
                  <div class="text-body-2">{{ formatValue(entry.info) }}</div>
                </div>
              </div>

              <div v-if="Object.keys(serverStatus(client.id).response.extended_data || {}).length" class="mt-3">
                <div class="font-weight-medium mb-1">Техническая информация</div>
                <div
                  v-for="(entry, key) in serverStatus(client.id).response.extended_data || {}"
                  :key="key"
                  class="status-row"
                  :title="plainText(entry.tooltip)"
                >
                  <v-icon icon="mdi-information-outline" :color="entryColor(entry)" size="18" />
                  <div>
                    <div class="text-caption text-medium-emphasis">{{ entry.title || key }}</div>
                    <div class="text-body-2">{{ formatValue(entry.info) }}</div>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="text-medium-emphasis py-5">Состояние ещё не запрашивалось.</div>
          </v-card-text>

        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog" max-width="900" persistent>
      <v-card>
        <v-card-title>{{ form.id ? 'Редактирование клиента' : 'Новый клиент' }}</v-card-title>
        <v-card-text>
          <v-form ref="clientForm">
            <v-row>
              <v-col cols="12" md="7">
                <v-text-field v-model="form.name" label="Название организации *" :rules="[requiredRule]" />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field v-model="form.url" label="URL" placeholder="https://college.example.ru" :rules="[urlRule]" />
              </v-col>
              <v-col cols="12" md="4"><v-text-field v-model="form.inn" label="ИНН" :rules="[digitsRule]" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="form.kpp" label="КПП" :rules="[digitsRule]" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="form.ogrn" label="ОГРН" :rules="[digitsRule]" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="form.okpo" label="ОКПО" :rules="[digitsRule]" /></v-col>
              <v-col cols="12" md="8">
                <v-select
                  v-model="form.director"
                  :items="directors"
                  item-title="name"
                  item-value="id"
                  label="Руководитель"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="6"><v-text-field v-model="form.client_admin_phone" label="Телефон системного администратора" /></v-col>
              <v-col cols="12" md="6"><v-text-field v-model="form.subordinate" label="Вышестоящий орган управления" /></v-col>
              <v-col cols="12" class="d-flex flex-wrap ga-5 py-0">
                <v-checkbox v-model="form.branch" label="Филиал" hide-details />
                <v-checkbox v-model="form.finance" label="Частная организация" hide-details />
                <v-checkbox v-model="form.hidden" label="Скрыть и не опрашивать" hide-details />
              </v-col>
            </v-row>
          </v-form>
          <v-alert v-if="formError" type="error" variant="tonal" density="compact" class="mt-3">{{ formError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="editDialog = false">Отмена</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveClient">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog ref="confirm" />
  </div>
</template>

<script>
import ConfirmDialog from 'moodleDir/procollege/lib/vue/confirm-dialog.vue';

const EMPTY_FORM = {
  id: 0,
  name: '',
  url: '',
  inn: '',
  kpp: '',
  ogrn: '',
  okpo: '',
  director: null,
  client_admin_phone: '',
  subordinate: '',
  branch: false,
  finance: false,
  hidden: false,
};

export default {
  name: 'ClientsInfoPage',
  components: { ConfirmDialog },
  data() {
    return {
      clients: [],
      directors: [],
      sesskey: '',
      statuses: {},
      clientsLoading: false,
      serversLoading: false,
      saving: false,
      editDialog: false,
      pageError: '',
      formError: '',
      form: { ...EMPTY_FORM },
      headers: [
        { title: 'Организация', key: 'name' },
        { title: 'URL', key: 'url' },
        { title: 'Руководитель', key: 'directorname' },
        { title: 'ИНН', key: 'inn' },
        { title: 'ОГРН', key: 'ogrn' },
        { title: 'Тип', key: 'type', sortable: false },
        { title: '', key: 'actions', sortable: false, align: 'end' },
      ],
      requiredRule: (value) => String(value || '').trim() !== '' || 'Обязательное поле',
      digitsRule: (value) => !value || /^\d+$/.test(String(value)) || 'Допустимы только цифры',
      urlRule: (value) => {
        if (!value) return true;
        try {
          const parsed = new URL(value);
          return ['http:', 'https:'].includes(parsed.protocol) || 'Укажите URL с http:// или https://';
        } catch (error) {
          return 'Укажите корректный URL';
        }
      },
    };
  },
  computed: {
    activeClients() {
      return this.clients.filter((client) => client.url && !this.toBoolean(client.hidden));
    },
  },
  async created() {
    await this.loadClients();
    await this.loadAllServerInfo();
  },
  methods: {
    async loadClients() {
      this.clientsLoading = true;
      this.pageError = '';
      try {
        const response = await this.axios.get(`${this.$model.wwwroot}/procollege/manage_groups/clientsInfo/getClients`);
        this.clients = Array.isArray(response.data?.clients) ? response.data.clients : [];
        this.directors = Array.isArray(response.data?.directors) ? response.data.directors : [];
        this.sesskey = response.data?.sesskey || window.M?.cfg?.sesskey || '';
      } catch (error) {
        this.pageError = this.errorMessage(error, 'Не удалось загрузить список клиентов.');
      } finally {
        this.clientsLoading = false;
      }
    },
    async loadAllServerInfo() {
      if (this.activeClients.length === 0) return;
      this.serversLoading = true;
      await Promise.allSettled(this.activeClients.map((client) => this.loadServerInfo(client)));
      this.serversLoading = false;
    },
    async loadServerInfo(client) {
      this.statuses[client.id] = { loading: true, response: null, error: '', code: '', checkedAt: null };
      const baseUrl = this.serverUrl(client.url);
      if (baseUrl) {
        try {
          const directResponse = await this.axios.get(
            `${baseUrl}/procollege/core_procollege/helpers/ServerInfo/get`,
            { timeout: 15000 },
          );
          if (directResponse.data && typeof directResponse.data === 'object') {
            this.statuses[client.id] = {
              loading: false,
              response: directResponse.data,
              error: '',
              code: directResponse.status,
              checkedAt: Date.now(),
            };
            return;
          }
        } catch {
          // CORS, mixed content, сетевая или HTTP-ошибка: повторяем запрос через главный сервер.
        }
      }

      try {
        const response = await this.axios.get(
          `${this.$model.wwwroot}/procollege/manage_groups/clientsInfo/getServerInfo`,
          { params: { id: client.id }, timeout: 35000 },
        );
        if (!response.data?.success) {
          this.statuses[client.id] = {
            loading: false,
            response: null,
            error: response.data?.message || 'Не удалось получить состояние сервера.',
            code: response.data?.code || 'PROXY_ERROR',
            checkedAt: Date.now(),
          };
          return;
        }
        if (!response.data.data || typeof response.data.data !== 'object') {
          throw new Error('Сервер вернул ответ в неизвестном формате.');
        }
        this.statuses[client.id] = {
          loading: false,
          response: response.data.data,
          error: '',
          code: response.data.code || response.status,
          checkedAt: Date.now(),
        };
      } catch (error) {
        this.statuses[client.id] = {
          loading: false,
          response: null,
          error: this.errorMessage(error, 'Не удалось получить состояние сервера.'),
          code: error.response?.status || error.code || 'NETWORK_ERROR',
          checkedAt: Date.now(),
        };
      }
    },
    openCreateDialog() {
      this.form = { ...EMPTY_FORM };
      this.formError = '';
      this.editDialog = true;
    },
    openEditDialog(client) {
      this.form = {
        ...EMPTY_FORM,
        ...client,
        id: Number(client.id),
        director: client.director ? Number(client.director) : null,
        branch: this.toBoolean(client.branch),
        finance: this.toBoolean(client.finance),
        hidden: this.toBoolean(client.hidden),
      };
      this.formError = '';
      this.editDialog = true;
    },
    async saveClient() {
      const validation = await this.$refs.clientForm.validate();
      if (!validation.valid) return;
      this.saving = true;
      this.formError = '';
      try {
        const payload = new URLSearchParams();
        for (const [key, value] of Object.entries(this.form)) {
          payload.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value ?? ''));
        }
        payload.set('sesskey', this.sesskey);
        await this.axios.post(`${this.$model.wwwroot}/procollege/manage_groups/clientsInfo/saveClient`, payload);
        this.editDialog = false;
        await this.loadClients();
        await this.loadAllServerInfo();
      } catch (error) {
        this.formError = this.errorMessage(error, 'Не удалось сохранить клиента.');
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(client) {
      const confirmed = await this.$refs.confirm.open(
        'Удаление клиента',
        `Удалить организацию «${this.escapeHtml(client.name)}»?`,
        { color: 'error' }
      );
      if (!confirmed) return;
      try {
        const payload = new URLSearchParams({ id: String(client.id), sesskey: this.sesskey });
        await this.axios.post(`${this.$model.wwwroot}/procollege/manage_groups/clientsInfo/deleteClient`, payload);
        delete this.statuses[client.id];
        await this.loadClients();
      } catch (error) {
        this.pageError = this.errorMessage(error, 'Не удалось удалить клиента.');
      }
    },
    serverStatus(id) {
      return this.statuses[id] || { loading: false, response: null, error: '', code: '', checkedAt: null };
    },
    statusIcon(id) {
      const status = this.serverStatus(id);
      if (status.loading) return 'mdi-progress-clock';
      if (status.error) return 'mdi-server-off';
      if (status.response && Object.keys(status.response.data || {}).length) return 'mdi-alert-circle';
      if (status.response) return 'mdi-check-circle';
      return 'mdi-server';
    },
    statusColor(id) {
      const status = this.serverStatus(id);
      if (status.error) return 'error';
      if (status.response && Object.keys(status.response.data || {}).length) return 'warning';
      if (status.response) return 'success';
      return 'grey';
    },
    errorMessage(error, fallback) {
      const data = error.response?.data;
      if (typeof data === 'string' && data.trim()) return data;
      if (data?.message) return data.message;
      if (data?.error) return data.error;
      if (error.message) return error.message;
      return fallback;
    },
    formatValue(value) {
      if (Array.isArray(value)) return value.join(' / ');
      if (value === null || value === undefined || value === '') return '—';
      return String(value);
    },
    serverUrl(value) {
      try {
        const parsed = new URL(String(value || ''));
        if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password || parsed.search || parsed.hash) return '';
        return String(value).replace(/\/+$/, '');
      } catch (error) {
        return '';
      }
    },
    entryColor(entry) {
      const color = String(entry?.color || '');
      if (color.includes('red')) return 'error';
      if (color.includes('green')) return 'success';
      return 'primary';
    },
    plainText(value) {
      return String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    toBoolean(value) {
      return value === true || value === 1 || value === '1';
    },
  },
};
</script>

<style scoped>
.clients-info-page {
  width: 100%;
}

.server-card {
  display: flex;
  flex-direction: column;
}

.server-card .v-card-text {
  flex: 1 1 auto;
}

.server-title {
  width: 100%;
  overflow-wrap: anywhere;
  white-space: normal;
  line-height: 1.5;
}

.server-card-header {
  display: block;
  font-size: inherit;
  white-space: normal;
}

.server-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.status-row {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  padding: 7px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  overflow-wrap: anywhere;
}

.status-row:last-child {
  border-bottom: 0;
}
</style>
